import { useMemo, useRef, useState } from 'react'
import {
  getFiscalYearLabel,
  parseBudgetGrid,
  planBudgetImport,
  readSpreadsheetGrid,
  SHORT_LABEL_MAX,
  sumDollars,
  type BudgetCode,
  type NewOwnedBudgetCode,
  type ParsedBudget,
  type PlannedImportLine,
} from '@budgetmonkey/shared'
import { Badge, Button, Card, ErrorNote, Input, Money } from '@/components/ui'
import { useApplyBudgetImport } from './hooks'

/**
 * Import budget codes from a building's budget spreadsheet.
 *
 * The file is read in the browser and nothing is written until the preview is
 * confirmed. Three decisions were Matt's, made on the district's 2026-27 Middle
 * School Budget:
 *
 *   - **Short labels are suggested, and editable.** Most descriptions are longer
 *     than the 24-character label, so each line gets a suggestion that is
 *     marked when it differs from the description, and flagged when two lines
 *     would end up with the same one.
 *   - **$0 lines are imported.** They are real codes; money can be moved onto
 *     them mid-year and charges filed against them.
 *   - **An existing code is shown, not overwritten.** Its current balance sits
 *     beside the file's, and it changes only if the box is ticked.
 */

interface LineChoice {
  /** For a new line: import it. For an existing one: update its balance. */
  include: boolean
  shortLabel: string
}

export function BudgetImport({
  fiscalYear,
  existingCodes,
  onDone,
}: {
  fiscalYear: number
  existingCodes: readonly BudgetCode[]
  onDone: () => void
}) {
  const [parsed, setParsed] = useState<{ fileName: string; budget: ParsedBudget }>()
  const [readError, setReadError] = useState<string>()
  const [reading, setReading] = useState(false)
  const [choices, setChoices] = useState<Record<string, LineChoice>>({})
  const [done, setDone] = useState<{ created: number; updated: number; total: number }>()

  const apply = useApplyBudgetImport(fiscalYear)

  const existingForImport = useMemo(
    () =>
      existingCodes.map((code) => ({
        id: code.id ?? '',
        code: code.code,
        beginningBalance: code.beginningBalance,
        external: code.external,
      })),
    [existingCodes],
  )

  const planned = useMemo(
    () => (parsed === undefined ? [] : planBudgetImport(parsed.budget.lines, existingForImport)),
    [parsed, existingForImport],
  )

  async function readFile(file: File) {
    setReading(true)
    setReadError(undefined)
    try {
      const bytes = new Uint8Array(await file.arrayBuffer())
      // The year being imported into decides which money column is read when
      // the workbook names years in its headings — otherwise "2025/26 Adopted
      // Budget" outscores "2026/27 Budget" on wording alone and last year's
      // balances arrive under this year's codes.
      const budget = parseBudgetGrid(readSpreadsheetGrid(bytes, file.name), fiscalYear)
      if (budget.lines.length === 0) {
        setReadError(
          budget.problems.length > 0
            ? `No line in ${file.name} could be imported — see the reasons below.`
            : `${file.name} has headings but no budget lines under them.`,
        )
      }

      // Ticks come from the same match the preview draws, so the two cannot
      // disagree about whether a line already exists. New lines start ticked;
      // an existing code's balance is left alone until someone chooses.
      const initial: Record<string, LineChoice> = {}
      for (const line of planBudgetImport(budget.lines, existingForImport)) {
        initial[line.accountCode] = {
          include: line.status === 'new',
          shortLabel: line.suggestedShortLabel,
        }
      }
      setChoices(initial)
      setParsed({ fileName: file.name, budget })
    } catch (cause) {
      setParsed(undefined)
      setReadError(cause instanceof Error ? cause.message : 'That file could not be read.')
    } finally {
      setReading(false)
    }
  }

  // ── Derived from the current choices ──────────────────────────────────────

  const creates = planned.filter(
    (line) => line.status === 'new' && choices[line.accountCode]?.include === true,
  )
  const updates = planned.filter(
    (line): line is Extract<PlannedImportLine, { status: 'exists' }> =>
      line.status === 'exists' &&
      choices[line.accountCode]?.include === true &&
      line.existingBalance !== line.amount,
  )

  /**
   * Labels used more than once — among the lines being created and the codes
   * already set up for this year. Two dashboard cards reading "Prof. Dev.
   * Dues/Fees" are two cards nobody can tell apart.
   */
  const duplicateLabels = useMemo(() => {
    const counts = new Map<string, number>()
    const bump = (label: string) => {
      const key = label.trim().toLowerCase()
      if (key !== '') counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    for (const code of existingCodes) if (!code.external && code.active) bump(code.shortLabel)
    for (const line of creates) bump(choices[line.accountCode]?.shortLabel ?? '')
    return new Set([...counts].filter(([, n]) => n > 1).map(([key]) => key))
  }, [existingCodes, creates, choices])

  const blankLabels = creates.filter(
    (line) => (choices[line.accountCode]?.shortLabel ?? '').trim() === '',
  ).length
  const duplicateCount = creates.filter((line) =>
    duplicateLabels.has((choices[line.accountCode]?.shortLabel ?? '').trim().toLowerCase()),
  ).length

  const createTotal = sumDollars(creates.map((line) => line.amount))
  const wrongYear =
    parsed?.budget.namedFiscalYear != null && parsed.budget.namedFiscalYear !== fiscalYear

  async function confirm() {
    const inputs: NewOwnedBudgetCode[] = creates.map((line) => ({
      code: line.accountCode,
      displayName: line.description,
      shortLabel: (choices[line.accountCode]?.shortLabel ?? '').trim(),
      // Every line in a building budget starts 10-, the general fund. A grant
      // code can be switched on its own page; guessing one from here would be
      // wrong far more often than right.
      fund: 'main',
      category: line.category,
      aliases: [],
      beginningBalance: line.amount,
      alertThresholdPercent: 80,
    }))

    await apply.mutateAsync({
      creates: inputs,
      updates: updates.map((line) => ({
        id: line.existingId,
        beginningBalance: line.amount,
      })),
    })
    setDone({ created: inputs.length, updated: updates.length, total: createTotal })
  }

  function setChoice(accountCode: string, patch: Partial<LineChoice>) {
    setChoices((previous) => ({
      ...previous,
      [accountCode]: { ...previous[accountCode]!, ...patch },
    }))
  }

  // ── Screens ───────────────────────────────────────────────────────────────

  if (done !== undefined) {
    return (
      <Card className="space-y-3">
        <h2 className="text-lg font-semibold">Imported into {getFiscalYearLabel(fiscalYear)}</h2>
        <p className="text-slate-700">
          {done.created} code{done.created === 1 ? '' : 's'} added, totalling{' '}
          <Money value={done.total} />
          {done.updated > 0 &&
            `, and ${done.updated} existing balance${done.updated === 1 ? '' : 's'} updated`}
          .
        </p>
        <p className="text-sm text-slate-600">
          Everything came in as the general fund with no aliases. Open a code to
          mark it as a grant, or to add the words you'd type to look it up.
        </p>
        <div>
          <Button variant="primary" onClick={onDone}>
            See your budget codes
          </Button>
        </div>
      </Card>
    )
  }

  if (parsed === undefined) {
    return (
      <div className="space-y-4">
        <FilePicker reading={reading} onFile={(file) => void readFile(file)} />
        {readError !== undefined && <ErrorNote>{readError}</ErrorNote>}
        <Card className="space-y-2 text-sm text-slate-600">
          <p className="font-medium text-slate-900">What the file needs</p>
          <p>
            A sheet with columns headed <b>Account Code</b>, <b>Description</b> and
            a budget amount — for example <b>2026/27 Proposed Budget</b>. Title
            rows above the headings are fine, and any other columns, including
            notes, are ignored.
          </p>
          <p>Nothing is saved until you've checked the preview.</p>
        </Card>
        <div>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  const { budget, fileName } = parsed

  return (
    <div className="space-y-5">
      <Card className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="min-w-0">
            <p className="break-words font-medium text-slate-900">
              {budget.titles.slice(-1)[0] ?? fileName}
            </p>
            <p className="break-words text-sm text-slate-500">
              {fileName} · read <b>{budget.columns.accountCode}</b>,{' '}
              <b>{budget.columns.description}</b> and <b>{budget.columns.amount}</b>
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={() => {
              setParsed(undefined)
              setReadError(undefined)
            }}
          >
            Choose a different file
          </Button>
        </div>

        {wrongYear && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            This file is the <b>{getFiscalYearLabel(budget.namedFiscalYear!)}</b> budget,
            but you're importing into <b>{getFiscalYearLabel(fiscalYear)}</b>. If that's
            not what you meant, cancel and switch the year in the header first.
          </p>
        )}
      </Card>

      {readError !== undefined && <ErrorNote>{readError}</ErrorNote>}

      {budget.problems.length > 0 && (
        <Card className="space-y-2">
          <p className="font-medium text-slate-900">
            {budget.problems.length} row{budget.problems.length === 1 ? '' : 's'} can't be
            imported
          </p>
          <ul className="space-y-1.5 text-sm">
            {budget.problems.map((problem, index) => (
              <li key={index} className="break-words">
                <span className="text-slate-900">{problem.row}</span>
                <span className="text-slate-500"> — {problem.reason}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {planned.length > 0 && (
        <section className="space-y-2" aria-label="Budget lines">
          <p className="text-sm text-slate-600">
            Short labels are shown on dashboard cards and in the code picker, so
            keep them to {SHORT_LABEL_MAX} characters. Suggested ones are marked —
            change any that don't read right.
          </p>
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {planned.map((line) => (
              <PreviewRow
                key={line.accountCode}
                line={line}
                choice={choices[line.accountCode]!}
                existingLabel={
                  line.status === 'new'
                    ? undefined
                    : existingCodes.find((c) => c.id === line.existingId)?.shortLabel
                }
                duplicate={
                  line.status === 'new' &&
                  duplicateLabels.has(
                    (choices[line.accountCode]?.shortLabel ?? '').trim().toLowerCase(),
                  )
                }
                onChange={(patch) => setChoice(line.accountCode, patch)}
              />
            ))}
          </ul>
        </section>
      )}

      <Card className="space-y-3">
        <p className="text-slate-900">
          <b>{creates.length}</b> new code{creates.length === 1 ? '' : 's'} totalling{' '}
          <Money value={createTotal} className="font-semibold" />
          {updates.length > 0 && (
            <>
              {' '}
              · <b>{updates.length}</b> balance{updates.length === 1 ? '' : 's'} updated
            </>
          )}
        </p>

        {duplicateCount > 0 && (
          <p className="text-sm text-amber-800">
            {duplicateCount} short label{duplicateCount === 1 ? ' is' : 's are'} shared with
            another code. You can import anyway, but those cards will be hard to tell apart.
          </p>
        )}
        {blankLabels > 0 && (
          <p className="text-sm text-red-700">
            {blankLabels} code{blankLabels === 1 ? ' needs' : 's need'} a short label first.
          </p>
        )}
        {apply.error !== null && <ErrorNote>{apply.error.message}</ErrorNote>}

        <div className="flex flex-wrap gap-3">
          <Button
            variant="primary"
            disabled={
              apply.isPending ||
              blankLabels > 0 ||
              (creates.length === 0 && updates.length === 0)
            }
            onClick={() => void confirm()}
          >
            {apply.isPending
              ? 'Importing…'
              : `Import into ${getFiscalYearLabel(fiscalYear)}`}
          </Button>
          <Button variant="ghost" onClick={onDone} disabled={apply.isPending}>
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  )
}

function PreviewRow({
  line,
  choice,
  existingLabel,
  duplicate,
  onChange,
}: {
  line: PlannedImportLine
  choice: LineChoice
  existingLabel: string | undefined
  duplicate: boolean
  onChange: (patch: Partial<LineChoice>) => void
}) {
  const id = `import-${line.accountCode}`
  const label = choice.shortLabel.trim()
  const differs = label !== line.description

  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        {line.status === 'new' && (
          <input
            id={`${id}-include`}
            type="checkbox"
            checked={choice.include}
            onChange={(e) => onChange({ include: e.target.checked })}
            className="mt-1 size-4 shrink-0"
            aria-label={`Import ${line.description}`}
          />
        )}
        <div className="min-w-0 flex-1">
          <p
            className={`break-words text-sm font-medium ${
              line.status === 'new' && !choice.include ? 'text-slate-400' : 'text-slate-900'
            }`}
          >
            {line.description}
          </p>
          <p className="break-all font-mono text-xs text-slate-500">{line.accountCode}</p>
        </div>
        <Money value={line.amount} className="text-sm tabular-nums" />
      </div>

      {line.status === 'new' && choice.include && (
        <div className="space-y-1 sm:pl-7">
          <label htmlFor={`${id}-label`} className="sr-only">
            Short label for {line.description}
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-full sm:w-64">
              <Input
                id={`${id}-label`}
                value={choice.shortLabel}
                maxLength={SHORT_LABEL_MAX}
                onChange={(e) => onChange({ shortLabel: e.target.value })}
                invalid={label === '' || duplicate}
              />
            </div>
            {label === '' ? (
              <span className="text-xs text-red-700">Needs a label</span>
            ) : duplicate ? (
              <Badge tone="warn">Same label as another code</Badge>
            ) : differs ? (
              <Badge>Shortened</Badge>
            ) : null}
          </div>
        </div>
      )}

      {line.status === 'exists' && (
        <div className="space-y-1 sm:pl-7">
          <p className="text-xs text-slate-500">
            Already set up{existingLabel !== undefined ? ` as "${existingLabel}"` : ''}, with a
            starting balance of <Money value={line.existingBalance} />.
          </p>
          {line.existingBalance === line.amount ? (
            <p className="text-xs text-slate-500">Same balance as the file — nothing to change.</p>
          ) : (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={choice.include}
                onChange={(e) => onChange({ include: e.target.checked })}
                className="size-4"
              />
              Update the starting balance to <Money value={line.amount} />
            </label>
          )}
        </div>
      )}

      {line.status === 'external' && (
        <p className="text-xs text-amber-800 sm:pl-7">
          Already set up as someone else's (external) code, so it's left as it is.
        </p>
      )}
    </li>
  )
}

function FilePicker({ reading, onFile }: { reading: boolean; onFile: (file: File) => void }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  return (
    <div
      onDragEnter={(e) => {
        e.preventDefault()
        setDragging(true)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        setDragging(false)
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        const file = e.dataTransfer.files[0]
        if (file !== undefined) onFile(file)
      }}
      className={`rounded-xl border-2 border-dashed px-6 py-10 text-center transition ${
        dragging ? 'border-slate-900 bg-slate-100' : 'border-slate-300 bg-white'
      }`}
    >
      <p className="font-medium text-slate-900">
        {reading ? 'Reading…' : 'Drop your budget spreadsheet here'}
      </p>
      <p className="mt-1 text-sm text-slate-600">.xlsx or .csv</p>
      <Button
        variant="primary"
        className="mt-4"
        onClick={() => inputRef.current?.click()}
        disabled={reading}
      >
        Choose file
      </Button>
      <input
        ref={inputRef}
        id="budget-import-file"
        type="file"
        accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file !== undefined) onFile(file)
        }}
      />
    </div>
  )
}
